import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useLocation } from 'wouter';
import { ActivityFeed } from '@/components/ActivityFeed/ActivityFeed';
import { CallsCard } from '@/components/CallsCard';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import { ClientCompaniesBlock } from '@/components/client-portfolio/ClientCompaniesBlock';
import Link from '@/components/Link';
import { MONDAY_BOARDS, mondayItemUrl } from '@/lib/monday';
import { useAuth } from '@/models/auth';
import { type Contact, useAddContactComment } from '@/models/contacts';
import {
  ARCHIVE_REASON_LABELS,
  type LeadStatus,
  MANUAL_LEAD_STATUSES,
  useAddLeadComment,
  useAddPersonMember,
  useRequestPersonAccess,
  useUpdateLeadStatus,
} from '@/models/leads';
import { useHasAnyPermission } from '@/models/permissions';
import {
  PERSON_STATE_COLORS,
  PERSON_STATE_LABELS,
  type PersonCardResponse,
  type PersonRequest,
} from '@/models/persons';
import { IdentityCard as ContactIdentityCard } from '@/pages/contacts/IdentityCard';
import { ImportNotesCard } from '@/pages/contacts/ImportNotesCard';
import { MembersControl } from '@/pages/contacts/MembersControl';
import MondayContactKycPanel from '@/pages/contacts/MondayContactKycPanel';
import { NotesCard } from '@/pages/contacts/NotesCard';
import { ReferralCard } from '@/pages/contacts/ReferralCard';
import { ActionsCard } from '@/pages/leads/ActionsCard';
import { FunnelCard } from '@/pages/leads/FunnelCard';
import { MarketingCard } from '@/pages/leads/MarketingCard';
import { QualifyLeadModal } from '@/pages/leads/QualifyLeadModal';
import { RequestNotesCard } from '@/pages/leads/RequestNotesCard';
import { StatusBadge } from '@/pages/leads/StatusBadge';
import CreateOfferModal from '@/pages/offers/CreateOfferModal';
import { DuplicatesCard } from '@/pages/persons/DuplicatesCard';
import { buildPersonFeed } from '@/pages/persons/feed';
import { ChannelsCard, TouchesCard } from '@/pages/persons/PersonExtras';

const fmtDate = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleDateString() : '-';

const CLOSED_LABELS: Record<string, string> = {
  ...ARCHIVE_REASON_LABELS,
  spam: 'spam',
  duplicate: 'duplicate',
  sql_disqualified: 'disqualified',
};

function StateBadge({ data }: { data: PersonCardResponse }) {
  const { state, closed } = data.state;
  const reason =
    state === 'closed' && closed
      ? [CLOSED_LABELS[closed.code] ?? closed.code, closed.reason]
          .filter(Boolean)
          .join(': ')
      : null;
  return (
    <Badge color={PERSON_STATE_COLORS[state]} variant="light">
      {PERSON_STATE_LABELS[state]}
      {reason ? ` - ${reason}` : ''}
    </Badge>
  );
}

function Header({ data }: { data: PersonCardResponse }) {
  const { user } = useAuth();
  const canAssign = useHasAnyPermission([
    'contacts:assign',
    'contacts:*',
    '*:*',
  ]);
  const requestAccess = useRequestPersonAccess();
  const addMember = useAddPersonMember();
  const p = data.person;
  const name =
    [p.firstName, p.lastName].filter(Boolean).join(' ') ||
    p.email ||
    `#${p.id}`;
  const open = data.state.openLead;
  const anyLead = data.requests[0]?.id ?? 0;
  return (
    <Card withBorder>
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Group gap="xs">
            <Title order={3}>{name}</Title>
            <StateBadge data={data} />
            {data.requests.length + data.hiddenRequests.length > 1 && (
              <Badge color="orange" variant="outline">
                Repeat request
              </Badge>
            )}
          </Group>
          {open && (
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                Open request:
              </Text>
              <Link to={`~/leads/${open.id}`}>#{open.id}</Link>
              <StatusBadge status={open.status as LeadStatus} />
            </Group>
          )}
          <Text size="xs" c="dimmed">
            Last contact: {fmtDate(p.lastContactAt)}
          </Text>
          {data.access.parts.allLeads && (
            <MembersControl
              contactId={p.id}
              members={data.members}
              readOnly={!canAssign}
            />
          )}
        </Stack>
        <Stack gap={6} align="flex-end">
          {data.access.canRequestAccess && (
            <Tooltip
              label="Notifies the members of this person and those who can assign. Opens nothing by itself."
              multiline
              w={260}
            >
              <Button
                size="xs"
                variant="light"
                loading={requestAccess.isPending}
                onClick={async () => {
                  const r = await requestAccess.mutateAsync({
                    contactId: p.id,
                    leadId: anyLead,
                  });
                  notifications.show({
                    color: 'green',
                    message: `Access requested - ${r.notified} notified`,
                  });
                }}
              >
                Request access
              </Button>
            </Tooltip>
          )}
          {canAssign && !data.access.isMember && user && (
            <Button
              size="xs"
              variant="light"
              loading={addMember.isPending}
              onClick={async () => {
                await addMember.mutateAsync({
                  contactId: p.id,
                  userId: Number(user.id),
                  leadId: anyLead,
                });
                notifications.show({
                  color: 'green',
                  message: 'You are now a member of this person',
                });
              }}
            >
              Add me as member
            </Button>
          )}
        </Stack>
      </Group>
    </Card>
  );
}

function RequestTitle({ lead }: { lead: PersonRequest }) {
  return (
    <Group gap="xs" wrap="wrap">
      <Text fw={600}>#{lead.id}</Text>
      <StatusBadge status={lead.status} />
      {lead.archivedAt && (
        <Badge color="gray" variant="outline">
          Closed at migration
          {lead.archiveReason
            ? `: ${ARCHIVE_REASON_LABELS[lead.archiveReason] ?? lead.archiveReason}`
            : ''}
        </Badge>
      )}
      {lead.isSpam && (
        <Badge color="red" variant="outline">
          Spam
        </Badge>
      )}
      {lead.duplicateOfLeadId && (
        <Badge color="gray" variant="outline">
          Duplicate of #{lead.duplicateOfLeadId}
        </Badge>
      )}
      {lead.sqlStatus && <Badge variant="outline">SQL {lead.sqlStatus}</Badge>}
      {lead.reason && (
        <Text size="sm" c="dimmed">
          {lead.reason}
        </Text>
      )}
      <Text size="sm" c="dimmed">
        {lead.source} · {fmtDate(lead.createdAt)} ·{' '}
        {lead.assigneeInfo?.name || lead.assigneeEmail || 'Unassigned'}
      </Text>
      {!lead.write && (
        <Badge color="gray" variant="light">
          Read only
        </Badge>
      )}
    </Group>
  );
}

function RequestPanel({
  lead,
  isMember,
  person,
}: {
  lead: PersonRequest;
  isMember: boolean;
  person: PersonCardResponse['person'];
}) {
  const { user } = useAuth();
  const updateStatus = useUpdateLeadStatus();
  const addMember = useAddPersonMember();
  const [statusDraft, setStatusDraft] = useState<LeadStatus | null>(null);
  const [qualifyOpen, setQualifyOpen] = useState(false);
  const canQualify = useHasAnyPermission([
    'contacts:write',
    'contacts:*',
    '*:*',
  ]);
  const readOnly = !lead.write;
  const showAddAssignee =
    isMember &&
    lead.assigneeUserId != null &&
    !lead.assigneeIsMember &&
    String(lead.assigneeUserId) !== String(user?.id);
  return (
    <Stack gap="sm">
      <Group gap="sm">
        {showAddAssignee && (
          <Button
            size="xs"
            variant="light"
            loading={addMember.isPending}
            onClick={async () => {
              await addMember.mutateAsync({
                contactId: Number(lead.contactId),
                userId: Number(lead.assigneeUserId),
                leadId: lead.id,
              });
              notifications.show({
                color: 'green',
                message: 'The assignee is now a member of this person',
              });
            }}
          >
            Add assignee as member
          </Button>
        )}
        {!readOnly && lead.status !== 'converted' && (
          <Group gap="xs">
            <Select
              size="xs"
              placeholder="Change stage"
              data={MANUAL_LEAD_STATUSES.filter((s) => s !== lead.status)}
              value={statusDraft}
              onChange={(v) => setStatusDraft(v as LeadStatus)}
              w={180}
            />
            <Button
              size="xs"
              disabled={!statusDraft}
              loading={updateStatus.isPending}
              onClick={async () => {
                await updateStatus.mutateAsync({
                  id: lead.id,
                  status: statusDraft as LeadStatus,
                });
                setStatusDraft(null);
              }}
            >
              Apply
            </Button>
          </Group>
        )}
      </Group>
      {!readOnly && (
        <ActionsCard
          lead={lead}
          canQualify={canQualify}
          onQualify={() => setQualifyOpen(true)}
        />
      )}
      {!readOnly && (
        <FunnelCard key={`funnel-${lead.id}-${lead.updatedAt}`} lead={lead} />
      )}
      <RequestNotesCard
        key={`notes-${lead.id}-${lead.updatedAt}`}
        lead={lead}
        readOnly={readOnly}
      />
      <MarketingCard
        key={`marketing-${lead.id}-${lead.updatedAt}`}
        lead={lead}
        readOnly={readOnly}
      />
      <QualifyLeadModal
        key={`qualify-${lead.id}`}
        lead={lead}
        person={person}
        opened={qualifyOpen}
        onClose={() => setQualifyOpen(false)}
      />
    </Stack>
  );
}

function Requests({ data }: { data: PersonCardResponse }) {
  const initial = data.requests
    .filter((r) => r.isOpen || r.id === Number(data.selectedLeadId))
    .map((r) => String(r.id));
  const [opened, setOpened] = useState<string[]>(initial);
  return (
    <Card withBorder>
      <Stack gap="xs">
        <Group justify="space-between">
          <Title order={5}>Requests</Title>
          <Text size="xs" c="dimmed">
            {data.requests.length + data.hiddenRequests.length}
          </Text>
        </Group>
        {data.requests.length === 0 && data.hiddenRequests.length === 0 && (
          <Text size="sm" c="dimmed">
            No requests yet.
          </Text>
        )}
        <Accordion
          multiple
          value={opened}
          onChange={setOpened}
          variant="contained"
        >
          {data.requests.map((r) => (
            <Accordion.Item key={r.id} value={String(r.id)}>
              <Accordion.Control
                bg={
                  r.id === Number(data.selectedLeadId)
                    ? 'var(--mantine-color-blue-0)'
                    : undefined
                }
              >
                <RequestTitle lead={r} />
              </Accordion.Control>
              <Accordion.Panel>
                {opened.includes(String(r.id)) && (
                  <RequestPanel
                    lead={r}
                    isMember={data.access.isMember}
                    person={data.person}
                  />
                )}
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
        {data.hiddenRequests.length > 0 && (
          <Text size="sm" c="dimmed">
            Other requests:{' '}
            {data.hiddenRequests.map((r) => fmtDate(r.createdAt)).join(', ')}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

function Offers({ data }: { data: PersonCardResponse }) {
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const canCreateOffer = useHasAnyPermission([
    'offers:write',
    'offers:*',
    '*:*',
  ]);
  const offers = data.offers ?? [];
  const p = data.person;
  const hasEmail = !!p.email?.trim();
  const contact = {
    ...p,
    accountId: Number(p.accountId),
    sourceLeadId: p.sourceLeadId ?? null,
    referringAgentId: p.referringAgentId ?? null,
    notes: p.notes ?? null,
  } as Contact;
  return (
    <CollapsibleCard
      title="Offers"
      rightSection={
        <Group gap="xs" wrap="nowrap">
          <Text size="xs" c="dimmed">
            {offers.length}
          </Text>
          {canCreateOffer && p.accountId != null && (
            <Button
              size="compact-xs"
              disabled={!hasEmail}
              title={
                hasEmail
                  ? undefined
                  : "Add the person's email to create an offer"
              }
              onClick={() => setOpen(true)}
            >
              New offer
            </Button>
          )}
        </Group>
      }
    >
      <Stack gap={4}>
        {p.accountId == null && (
          <Text size="sm" c="orange.8">
            Qualify a request of this person to make an offer.
          </Text>
        )}
        {offers.length === 0 && (
          <Text size="sm" c="dimmed">
            No offers yet.
          </Text>
        )}
        {offers.map((o) => (
          <Group key={o.id} justify="space-between">
            <Link to={`~/offers/${o.id}`}>{o.title}</Link>
            <Text size="xs" c={o.accepted ? 'green' : 'dimmed'}>
              {o.accepted ? 'accepted' : 'open'}
            </Text>
          </Group>
        ))}
      </Stack>
      <CreateOfferModal
        opened={open}
        onClose={() => setOpen(false)}
        initialContact={contact}
        onCreated={(offer) => {
          setOpen(false);
          navigate(`~/offers/${offer.id}`);
        }}
      />
    </CollapsibleCard>
  );
}

function Kyc({ pulse }: { pulse: string }) {
  const [show, setShow] = useState(false);
  return (
    <CollapsibleCard title="KYC" defaultOpen={false} onOpenChange={setShow}>
      {show && <MondayContactKycPanel pulseId={pulse} />}
    </CollapsibleCard>
  );
}

function MondayLinks({ data }: { data: PersonCardResponse }) {
  const items: [string, number, string | number][] = [];
  if (data.person.mondayItemId) {
    items.push([
      'Monday contact',
      MONDAY_BOARDS.CONTACTS,
      data.person.mondayItemId,
    ]);
  }
  for (const r of data.requests) {
    if (r.mondayItemId) {
      items.push([
        `Monday lead (#${r.id})`,
        MONDAY_BOARDS.LEADS,
        r.mondayItemId,
      ]);
    }
  }
  return (
    <CollapsibleCard title="Monday links" defaultOpen={false}>
      <Stack gap={4}>
        {items.map(([label, board, id]) => (
          <Anchor
            key={`${label}-${id}`}
            href={mondayItemUrl(board, id)}
            target="_blank"
            size="sm"
          >
            {label}: {id}
          </Anchor>
        ))}
        <Text size="xs" c="dimmed">
          Archived Monday relations appear here after the transfer.
        </Text>
      </Stack>
    </CollapsibleCard>
  );
}

export function PersonView({ data }: { data: PersonCardResponse }) {
  const addPersonComment = useAddContactComment();
  const addLeadComment = useAddLeadComment();
  const [emailFocus, setEmailFocus] = useState(0);
  const { access, person } = data;
  const money = access.parts.money;
  const commentLead =
    data.requests.find(
      (r) => r.id === Number(data.selectedLeadId) && r.write,
    ) ?? data.requests.find((r) => r.write && r.isOpen);
  const onPostComment = access.canEdit
    ? (body: string) =>
        addPersonComment.mutateAsync({ contactId: person.id, body })
    : commentLead
      ? (body: string) =>
          addLeadComment.mutateAsync({ leadId: commentLead.id, body })
      : undefined;
  const contact = {
    ...person,
    accountId: Number(person.accountId),
    sourceLeadId: person.sourceLeadId ?? null,
    referringAgentId: person.referringAgentId ?? null,
    notes: person.notes ?? null,
  } as Contact;

  return (
    <Paper p="md" h="100%">
      <Container size="xl" px={0}>
        <Grid>
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Stack gap="md">
              <Header data={data} />
              {!money && (
                <Alert color="gray" variant="light">
                  Offers, invoices, account, payers and KYC of this person are
                  visible to its members only.
                </Alert>
              )}
              <Requests data={data} />
              <ActivityFeed
                items={buildPersonFeed(data.feed)}
                onPostComment={onPostComment}
                posting={addPersonComment.isPending || addLeadComment.isPending}
              />
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack gap="md">
              {money && (
                <ImportNotesCard
                  notes={data.importNotes}
                  onAddEmail={() => setEmailFocus((n) => n + 1)}
                />
              )}
              {access.canEdit ? (
                <ContactIdentityCard
                  key={`identity-${person.id}-${person.updatedAt}`}
                  contact={contact}
                  focusEmailRequest={emailFocus}
                />
              ) : null}
              <DuplicatesCard contactId={Number(person.id)} />
              <ChannelsCard data={data} />
              <TouchesCard data={data} />
              {access.canEdit && (
                <NotesCard
                  key={`notes-${person.id}-${person.updatedAt}`}
                  contact={contact}
                />
              )}
              {access.parts.allLeads && (
                <ReferralCard
                  key={`referral-${person.id}-${person.updatedAt}`}
                  contact={contact}
                  readOnly={!access.canEdit}
                />
              )}
              {money && data.account && (
                <CollapsibleCard title="Account">
                  <Stack gap={2}>
                    <Link to={`~/accounts/${data.account.id}`}>
                      {data.account.name}
                    </Link>
                    {data.account.mondayStatus && (
                      <Text size="xs" c="dimmed">
                        Monday status: {data.account.mondayStatus}
                        {data.account.mondayType
                          ? ` · ${data.account.mondayType}`
                          : ''}
                      </Text>
                    )}
                  </Stack>
                </CollapsibleCard>
              )}
              {money && <Offers data={data} />}
              {money && (
                <ClientCompaniesBlock
                  contactId={person.id}
                  showContactServices
                />
              )}
              {money && person.mondayItemId && (
                <Kyc pulse={String(person.mondayItemId)} />
              )}
              {access.parts.touches && <CallsCard contactId={person.id} />}
              {access.parts.allLeads && <MondayLinks data={data} />}
            </Stack>
          </Grid.Col>
        </Grid>
      </Container>
    </Paper>
  );
}
